import { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import { debounce } from "lodash";

import WalletDwTable from "Common/Tables/WalletDwTable";
import Loader from "Common/Loader";
import { authServices } from "Utils/auth/services";
import { currentDateTime, getStartTimeOfDay } from "Utils/datefilter";
import { generateOptions } from "Utils/searchFilter";

interface StatementProps {
  isReport?: boolean;
  filter: boolean;
  activeTab: string;
}

const formatDateToString = (date: Date | string): string => {
  if (typeof date === "string") return date;
  const pad = (n: number) => (n < 10 ? "0" + n : n);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const WalletDw = ({ filter, activeTab }: StatementProps) => {
  const [usersData, setUsersData] = useState<SearchUsersListData[]>([]);

  const today = new Date();
  const initialStartDate = getStartTimeOfDay(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000));
  const initialEndDate = currentDateTime();

  const [statementData, setStatementData] = useState<StatementListData[]>([]);
  const [startDate, setStartDate] = useState<Date | string>(initialStartDate);
  const [endDate, setEndDate] = useState<Date | string>(initialEndDate);
  const [limit, setLimit] = useState<number>(50);
  const [page, setPage] = useState<number>(1);
  const [listTotal, setListTotal] = useState<ProfitLossTotal>({ total: 0, page: 0 });
  const [inputValue, setInputValue] = useState("");
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const userOptions = useMemo(
    () => generateOptions(usersData || [], "_id", "user_name"),
    [usersData]
  );

  const fetchOptions = useMemo(
    () =>
      debounce(async (input: string) => {
        if (input.length >= 3) {
          const { response } = await authServices.getUserList({ user_name: input });
          setUsersData(response?.data || []);
        }
      }, 300),
    []
  );

  useEffect(() => {
    fetchOptions(inputValue);
  }, [inputValue]);

  const handleFetchData = async (filters: any = {}) => {
    setIsLoading(true);
    const payload = {
      limit,
      page,
      from_date: formatDateToString(startDate),
      to_date: formatDateToString(endDate),
      ...(selectedUser && { user_id: selectedUser }),
      ...filters,
    };

    const { response } = await authServices.getwalletDWTransactionList(payload);
    setIsLoading(false);

    if (response?.data?.length > 0) {
      setStatementData(response.data || []);
      setListTotal({
        total: response?.metadata?.[0]?.total / response?.metadata?.[0]?.limit || 0,
        page: response?.metadata?.[0]?.page || 1,
      });
    } else {
      setStatementData([]);
      setListTotal({ total: 0, page: 1 });
    }
  };

  useEffect(() => {
    if (activeTab === "Wallet-DW") {
      handleFetchData();
    }
  }, [activeTab]);

  const handleFilterSubmit = () => {
    setPage(1);
    handleFetchData({ page: 1 });
  };

  const handleClearSelection = () => {
    const resetStart = getStartTimeOfDay(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
    const resetEnd = currentDateTime();

    setPage(1);
    setStartDate(resetStart);
    setEndDate(resetEnd);
    setSelectedUser("");
    setUsersData([]);

    handleFetchData({
      from_date: formatDateToString(resetStart),
      to_date: formatDateToString(resetEnd),
      page: 1,
    });
  };

  const handlePageFilter = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
    handleFetchData({ page: newPage, limit: newLimit });
  };

  return (
    <>
      {filter && (
        <Card>
          <Card.Body>
            <Form>
              <Row className="gy-3 align-items-end">
                <Col lg={9}>
                  <Row className="gap-3 gap-sm-0">
                    <Col lg={4}>
                      <Form.Label>Start Date</Form.Label>
                      <Flatpickr
                        className="form-control"
                        options={{
                          enableTime: true,
                          dateFormat: "Y-m-d H:i:S",
                          maxDate: endDate,
                        }}
                        value={startDate}
                        onChange={([date]: [Date]) => setStartDate(date)}
                      />
                    </Col>
                    <Col lg={4}>
                      <Form.Label>End Date</Form.Label>
                      <Flatpickr
                        className="form-control"
                        options={{
                          enableTime: true,
                          dateFormat: "Y-m-d H:i:S",
                          maxDate: currentDateTime(),
                        }}
                        value={endDate}
                        onChange={([date]: [Date]) => setEndDate(date)}
                      />
                    </Col>
                    {/* <Col lg={4}>
                      <Form.Label>Search By Username</Form.Label>
                      <Select
                        isClearable
                        options={userOptions}
                        value={userOptions.find((opt) => opt.value === selectedUser) || null}
                        className="customSelect"
                        onInputChange={(input: any) =>
                          setInputValue(input?.trim()?.toLowerCase())
                        }
                        onChange={(e: any) => setSelectedUser(e?.value || "")}
                      />
                    </Col> */}
                  </Row>
                </Col>
                <Col lg={3}>
                  <Button
                    className="me-2 btn-sm"
                    style={{ height: "38px" }}
                    onClick={handleFilterSubmit}
                  >
                    <i className="fs-lg align-middle ri-filter-line"></i> Submit
                  </Button>
                  <Button
                    variant="outline-primary"
                    className="btn-sm"
                    style={{ height: "38px" }}
                    onClick={handleClearSelection}
                  >
                    <i className="fs-lg align-middle ri-refresh-line"></i> Clear
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>
      )}
      <Card>
        <Card.Body>
          <WalletDwTable
            statementData={statementData}
            page={page}
            limit={limit}
            listTotal={listTotal}
            startDate={startDate}
            endDate={endDate}
            handlePageFilter={handlePageFilter}
          />
        </Card.Body>
      </Card>
      {isLoading && <Loader />}
    </>
  );
};

export default WalletDw;
